import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Modal, Select, Typography, Tag, DatePicker } from 'antd'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { approvalsApi, reportsApi } from '@/api/services'
import { Approval, Statistics } from '@/types'
import dayjs from 'dayjs'
import { leapTablePagination } from '@/components/table/defaultPagination'
import { approvalRowClass } from '@/components/table/statusRowClass'
import { buildViewActionColumn, translateApprovalColumns } from '@/components/table/approvalTableColumns'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import { isTableChromeTarget } from '@/components/table/tableChrome'
import { Button } from '@/components/ui/Button'
import { toast } from 'sonner'
import { DocumentPreviewSummary } from '@/components/approvals/DocumentPreviewSummary'
import { useTranslation } from 'react-i18next'
import {
  PageShell,
  PageHero,
  DashboardPanel,
  DashboardSection,
  PodStatusKpiGrid,
  PageLoadingSkeleton,
} from '@/components/dashboard'
import { BarChart3, Loader2 } from 'lucide-react'

const { Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

const PODReportsPage: React.FC = () => {
  const navigate = useNavigate()
  const typeSelectRef = useRef<any>(null)
  const statusSelectRef = useRef<any>(null)
  const customerSelectRef = useRef<any>(null)
  const transporterSelectRef = useRef<any>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation(['ReportsPOD', 'Common', 'PendingPOD', 'UploadPOD'])
  const [draftFilters, setDraftFilters] = useState({
    status: [] as string[],
    type: [] as string[],
    customerCodes: [''] as string[],
    transporterCodes: [''] as string[],
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
  })
  const [activeFilters, setActiveFilters] = useState({
    status: [] as string[],
    type: [] as string[],
    customerCode: '',
    transporterCode: '',
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
  })

  useEffect(() => {
    const rawStatus = searchParams.get('status')
    const statusNext = rawStatus ? rawStatus.split(',').filter((s) => ['Pending', 'Approved', 'Rejected'].includes(s)) : []

    const rawType = searchParams.get('type')
    const typeNext = rawType ? rawType.split(',').filter(Boolean) : []

    const rawCustomerCode = searchParams.get('customerCode') || ''
    const customerCodesNext = rawCustomerCode ? rawCustomerCode.split(',').filter(Boolean) : []

    const rawTransporterCode = searchParams.get('transporterCode') || ''
    const transporterCodesNext = rawTransporterCode ? rawTransporterCode.split(',').filter(Boolean) : []

    const dateFrom = searchParams.get('dateFrom')
    const dateTo = searchParams.get('dateTo')
    const dateRangeNext = dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] as [dayjs.Dayjs, dayjs.Dayjs] : null

    setDraftFilters({
      status: statusNext,
      type: typeNext,
      customerCodes: customerCodesNext,
      transporterCodes: transporterCodesNext,
      dateRange: dateRangeNext,
    })

    setActiveFilters({
      status: statusNext,
      type: typeNext,
      customerCode: rawCustomerCode,
      transporterCode: rawTransporterCode,
      dateRange: dateRangeNext,
    })
  }, [searchParams])

  const handleTypeChange = (value: string[]) => {
    if (value.includes('ALL')) {
      if (!draftFilters.type.includes('ALL')) {
        setDraftFilters({ ...draftFilters, type: ['ALL'] })
      } else {
        const filtered = value.filter((val) => val !== 'ALL')
        setDraftFilters({ ...draftFilters, type: filtered })
      }
    } else {
      setDraftFilters({ ...draftFilters, type: value })
    }
  }

  const applyReportsStatusFilter = (status: string[] | string) => {
    const statusArr = Array.isArray(status)
      ? status.filter(Boolean)
      : [status].filter(Boolean) as string[]
    
    setSearchParams(
      (prev) => {
        const q = new URLSearchParams(prev)
        if (statusArr.length > 0) q.set('status', statusArr.join(','))
        else q.delete('status')
        return q
      },
      { replace: true }
    )
  }
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [viewUrl, setViewUrl] = useState<string | null>(null)
  const [viewRecord, setViewRecord] = useState<Approval | null>(null)

  const [tableFilters, setTableFilters] = useState<Record<string, any>>({})
  const [documentNoQuery, setDocumentNoQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    setCurrentPage(1)
  }, [activeFilters, documentNoQuery])

  const { data: approvals, isLoading, isFetching } = useQuery({
    queryKey: ['approvals-reports', activeFilters, documentNoQuery],
    queryFn: () =>
      approvalsApi.getApprovals({
        status: activeFilters.status.length > 0 ? activeFilters.status.join(',') : undefined,
        type: (activeFilters.type.length > 0 && !activeFilters.type.includes('ALL')) ? activeFilters.type.join(',') : undefined,
        customerCode: activeFilters.customerCode || undefined,
        transporterCode: activeFilters.transporterCode || undefined,
        dateFrom: activeFilters.dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: activeFilters.dateRange?.[1]?.format('YYYY-MM-DD'),
        documentNo: documentNoQuery || undefined,
        limit: 1000,
      }),
    select: (response) => response.data,
    placeholderData: keepPreviousData,
  })

  const { data: statistics } = useQuery({
    queryKey: ['statistics', activeFilters, documentNoQuery],
    queryFn: () =>
      reportsApi.getStatistics({
        dateFrom: activeFilters.dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: activeFilters.dateRange?.[1]?.format('YYYY-MM-DD'),
        type: (activeFilters.type.length > 0 && !activeFilters.type.includes('ALL')) ? activeFilters.type.join(',') : undefined,
        customerCode: activeFilters.customerCode || undefined,
        transporterCode: activeFilters.transporterCode || undefined,
        documentNo: documentNoQuery || undefined,
      }),
    select: (response) => response.data,
    placeholderData: keepPreviousData,
  })

  const resolveAttachmentUrl = (record: Approval): string | null =>
    record.tempUrl || record.newUrl || record.url || null

  const handleViewDocument = (record: Approval) => {
    const url = resolveAttachmentUrl(record)
    if (!url) {
      toast.error(t('PendingPOD:messages.noAttachmentAvailable', 'No attachment available'))
      return
    }
    setViewRecord(record)
    setViewUrl(url)
    setViewModalOpen(true)
  }

  const goToDocument = (record: Approval) => {
    navigate(`/documentJourney/${record.documentNo}`)
  }

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    const toastId = toast.loading('Preparing report export...')
    try {
      const blob = await reportsApi.exportToExcel({
        status: activeFilters.status.length > 0 ? activeFilters.status.join(',') : undefined,
        type: (activeFilters.type.length > 0 && !activeFilters.type.includes('ALL')) ? activeFilters.type.join(',') : undefined,
        customerCode: activeFilters.customerCode || undefined,
        transporterCode: activeFilters.transporterCode || undefined,
        dateFrom: activeFilters.dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: activeFilters.dateRange?.[1]?.format('YYYY-MM-DD'),
        documentNo: documentNoQuery || undefined,
      })

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `POD_Report_${dayjs().format('YYYY-MM-DD')}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      toast.success('Report downloaded successfully', { id: toastId })
    } catch (error: any) {
      console.error('Export failed:', error)
      let msg = 'Failed to export report'
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const json = JSON.parse(text)
          msg = json.detail || json.message || json.error || msg
        } catch {
          // ignore parsing error
        }
      } else if (error?.message) {
        msg = error.message
      }
      toast.error(msg, { id: toastId })
    } finally {
      setIsExporting(false)
    }
  }

  const handleFilter = () => {
    const activeCustomerCode = draftFilters.customerCodes.filter((c) => c.trim()).join(',')
    const activeTransporterCode = draftFilters.transporterCodes.filter((c) => c.trim()).join(',')

    setActiveFilters({
      status: draftFilters.status,
      type: draftFilters.type,
      customerCode: activeCustomerCode,
      transporterCode: activeTransporterCode,
      dateRange: draftFilters.dateRange,
    })

    setSearchParams(
      (prev) => {
        const q = new URLSearchParams(prev)
        if (draftFilters.status.length > 0) q.set('status', draftFilters.status.join(','))
        else q.delete('status')

        if (draftFilters.type.length > 0) q.set('type', draftFilters.type.join(','))
        else q.delete('type')

        if (activeCustomerCode) q.set('customerCode', activeCustomerCode)
        else q.delete('customerCode')

        if (activeTransporterCode) q.set('transporterCode', activeTransporterCode)
        else q.delete('transporterCode')

        if (draftFilters.dateRange?.[0] && draftFilters.dateRange?.[1]) {
          q.set('dateFrom', draftFilters.dateRange[0].format('YYYY-MM-DD'))
          q.set('dateTo', draftFilters.dateRange[1].format('YYYY-MM-DD'))
        } else {
          q.delete('dateFrom')
          q.delete('dateTo')
        }
        return q
      },
      { replace: true }
    )
  }

  const rows = (approvals as any)?.data ?? []
  const stats: Statistics | undefined = (statistics as any)?.data

  const customerOptions = useMemo(() => {
    const map = new Map<string, string>()
    rows.forEach((r: Approval) => {
      if (r.customerCode) {
        map.set(r.customerCode, r.customerName || r.customerCode)
      }
    })
    return Array.from(map.entries()).map(([code, name]) => ({
      value: code,
      label: `${code} — ${name}`,
    }))
  }, [rows])

  const transporterOptions = useMemo(() => {
    const map = new Map<string, string>()
    rows.forEach((r: Approval) => {
      if (r.transporterCode) {
        map.set(r.transporterCode, r.transporterName || r.transporterCode)
      }
    })
    return Array.from(map.entries()).map(([code, name]) => ({
      value: code,
      label: `${code} — ${name}`,
    }))
  }, [rows])

  const metrics = useMemo(() => {
    const total = Number(stats?.total ?? 0)
    const pending = Number(stats?.pending ?? 0)
    const approved = Number(stats?.approved ?? 0)
    const rejected = Number(stats?.rejected ?? 0)
    return { total, pending, approved, rejected }
  }, [stats?.approved, stats?.pending, stats?.rejected, stats?.total])

  const isPdf = useMemo(() => (viewUrl ? /\.pdf($|\?)/i.test(viewUrl) : false), [viewUrl])

  const handleTableChange = (pagination: any, filters: any, _sorter: any, extra: any) => {
    setTableFilters(filters)

    if (extra?.action === 'filter') {
      setCurrentPage(1)
    } else if (extra?.action === 'paginate') {
      setCurrentPage(pagination.current || 1)
    }

    const searchedDocNo = filters.documentNo?.[0] ? String(filters.documentNo[0]).trim() : ''

    if (!searchedDocNo) {
      setDocumentNoQuery('')
      return
    }

    const rawList = (approvals as any)?.data ?? []
    const isFound = rawList.some((r: Approval) => {
      const docNoStr = String(r.documentNo ?? '')
      return docNoStr.toLowerCase().includes(searchedDocNo.toLowerCase())
    })

    if (isFound) {
      // If found in the current 1000 loaded list, use client-side filter by keeping query empty
      setDocumentNoQuery('')
    } else {
      setDocumentNoQuery(searchedDocNo)
    }
  }

  const columns = useMemo(() => {
    const baseCols = translateApprovalColumns(t)
    const mappedCols = baseCols.map((col) => {
      const keyStr = col.key ? String(col.key) : ''
      if (keyStr && keyStr in tableFilters) {
        return {
          ...col,
          filteredValue: tableFilters[keyStr] || null,
        }
      }
      return col
    })
    return [
      ...mappedCols,
      buildViewActionColumn({ t, onView: handleViewDocument }),
    ]
  }, [t, tableFilters])

  if (isLoading && !approvals) {
    return (
      <PageShell>
        <PageLoadingSkeleton />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHero
        title={t('ReportsPOD:header.title')}
        subtitle={t('ReportsPOD:header.description')}
        badges={[{ label: t('ReportsPOD:header.title'), accent: true, icon: BarChart3 }]}
        actions={
          <Button variant="outline" onClick={handleExport} disabled={!rows.length || isExporting}>
            {isExporting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Exporting…
              </>
            ) : (
              t('ReportsPOD:actions.excelReport')
            )}
          </Button>
        }
      />

      <PodStatusKpiGrid
        metrics={metrics}
        labels={{
          total: t('ReportsPOD:kpi.kpiTotal'),
          pending: t('ReportsPOD:kpi.kpiPending'),
          approved: t('ReportsPOD:kpi.kpiApproved'),
          rejected: t('ReportsPOD:kpi.kpiRejected'),
        }}
        onTotalClick={() => applyReportsStatusFilter('')}
        onPendingClick={() => applyReportsStatusFilter('Pending')}
        onApprovedClick={() => applyReportsStatusFilter('Approved')}
        onRejectedClick={() => applyReportsStatusFilter('Rejected')}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-3">
          <DashboardSection title={t('ReportsPOD:filters.filterTitle')} subtitle={t('ReportsPOD:filters.filterDesc')}>
            <DashboardPanel>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">{t('ReportsPOD:filters.filterDocType')}</label>
                  <Select
                    ref={typeSelectRef}
                    onSelect={() => typeSelectRef.current?.blur()}
                    mode="multiple"
                    value={draftFilters.type}
                    onChange={handleTypeChange}
                    className="w-full"
                    allowClear
                    placeholder={t('ReportsPOD:filters.filterAllTypes')}
                  >
                    <Option value="ALL">{t('ReportsPOD:filters.all', 'All')}</Option>
                    <Option value="ALT">{t('ReportsPOD:types.typeAllotment')}</Option>
                    <Option value="DHR">{t('ReportsPOD:types.typeDhr')}</Option>
                    <Option value="GRN">{t('ReportsPOD:types.typeGrn')}</Option>
                    <Option value="REL_OUT">{t('ReportsPOD:types.typeRelOut')}</Option>
                    <Option value="TS">{t('ReportsPOD:types.typeTradingSale', 'Trading Sale(TS)')}</Option>
                    <Option value="job_work" disabled>{t('ReportsPOD:types.typeJobWork', 'job_work')}</Option>
                    <Option value="trading_cell" disabled>{t('ReportsPOD:types.typeTradingCell', 'trading_cell')}</Option>
                  </Select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">{t('ReportsPOD:filters.filterCustomerCode', 'Customer Code')}</label>
                  <Select
                    ref={customerSelectRef}
                    onSelect={() => customerSelectRef.current?.blur()}
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    value={draftFilters.customerCodes}
                    onChange={(value) => setDraftFilters({ ...draftFilters, customerCodes: value })}
                    className="w-full"
                    allowClear
                    placeholder={t('ReportsPOD:filters.filterAllCustomers', 'All customers')}
                    options={customerOptions}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">{t('ReportsPOD:filters.filterTransporterCode', 'Transporter Code')}</label>
                  <Select
                    ref={transporterSelectRef}
                    onSelect={() => transporterSelectRef.current?.blur()}
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    value={draftFilters.transporterCodes}
                    onChange={(value) => setDraftFilters({ ...draftFilters, transporterCodes: value })}
                    className="w-full"
                    allowClear
                    placeholder={t('ReportsPOD:filters.filterAllTransporters', 'All transporters')}
                    options={transporterOptions}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">{t('ReportsPOD:filters.filterStatus')}</label>
                  <Select
                    ref={statusSelectRef}
                    onSelect={() => statusSelectRef.current?.blur()}
                    mode="multiple"
                    value={draftFilters.status}
                    onChange={(value) => setDraftFilters({ ...draftFilters, status: value })}
                    className="w-full"
                    allowClear
                    placeholder={t('ReportsPOD:filters.filterAllStatuses')}
                  >
                    <Option value="Pending">{t('ReportsPOD:status.Pending')}</Option>
                    <Option value="Approved">{t('ReportsPOD:status.Approved')}</Option>
                    <Option value="Rejected">{t('ReportsPOD:status.Rejected')}</Option>
                  </Select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">{t('ReportsPOD:filters.filterDateRange')}</label>
                  <RangePicker
                    format="DD-MM-YYYY"
                    placeholder={['DD-MM-YYYY', 'DD-MM-YYYY']}
                    inputReadOnly={false}
                    value={draftFilters.dateRange}
                    onChange={(dates) =>
                      setDraftFilters({ ...draftFilters, dateRange: (dates as [dayjs.Dayjs, dayjs.Dayjs] | null) ?? null })
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <Button type="button" className="w-full sm:w-auto" onClick={handleFilter}>
                    {t('ReportsPOD:filters.filterApply')}
                  </Button>
                </div>
              </div>
            </DashboardPanel>
          </DashboardSection>

          <DashboardSection title={t('ReportsPOD:breakdown.breakdownTitle')} subtitle={t('ReportsPOD:breakdown.breakdownDesc')}>
            <DashboardPanel>
              <div className="space-y-2">
                {stats?.byType && Object.keys(stats.byType).length > 0 ? (
                  Object.entries(stats.byType).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between gap-2">
                      <Tag
                        color={
                          type === 'ALT'
                            ? 'blue'
                            : type === 'DHR'
                              ? 'green'
                              : type === 'GRN'
                                ? 'orange'
                                : type === 'REL_OUT'
                                  ? 'purple'
                                  : 'cyan'
                        }
                      >
                        {type === 'ALT'
                          ? t('ReportsPOD:types.typeAllotment', 'Allotment')
                          : type === 'DHR'
                            ? t('ReportsPOD:types.typeDhr', 'Delivery Challan')
                            : type === 'GRN'
                              ? t('ReportsPOD:types.typeGrn', 'Goods Receipt Note')
                              : type === 'REL_OUT'
                                ? t('ReportsPOD:types.typeRelOut', 'Relocation/Stock Transfer')
                                : t('ReportsPOD:types.typeTradingSale', 'Trading Sale')}
                      </Tag>
                      <Text strong>{count}</Text>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground">{t('ReportsPOD:breakdown.breakdownNoData')}</div>
                )}
              </div>
            </DashboardPanel>
          </DashboardSection>
        </div>

        <div className="lg:col-span-9">
          <DashboardPanel
            title={t('ReportsPOD:table.tableTitle')}
            subtitle={t('ReportsPOD:table.tableDesc')}
            flush
            className="leap-data-page-card"
            actions={
              <Button type="button" variant="outline" size="sm" onClick={handleExport} disabled={!rows.length || isExporting}>
                {isExporting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Exporting…
                  </>
                ) : (
                  t('ReportsPOD:actions.excelReport')
                )}
              </Button>
            }
          >
            <LeapDataTable<Approval>
              dataSource={rows}
              columns={columns}
              rowKey="id"
              loading={isLoading || isFetching}
              rowClassName={record => approvalRowClass(record.status)}
              pagination={{
                ...leapTablePagination(rows.length),
                current: currentPage,
              }}
              onChange={handleTableChange}
              onRow={record => ({
                onClick: e => {
                  if (isTableChromeTarget(e.target)) return
                  goToDocument(record)
                },
                style: { cursor: 'pointer' },
              })}
            />
          </DashboardPanel>
        </div>
      </div>

      <Modal
        title={t('ReportsPOD:modals.viewTitle')}
        open={viewModalOpen}
        onCancel={() => {
          setViewModalOpen(false)
          setViewUrl(null)
          setViewRecord(null)
        }}
        footer={null}
        width="min(1200px, 98vw)"
        destroyOnClose
      >
        {viewUrl ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            {viewRecord ? <DocumentPreviewSummary record={viewRecord} /> : null}
            <div className="min-h-0 min-w-0 flex-1">
              {isPdf ? (
                <iframe src={viewUrl} className="w-full" style={{ height: '75vh', border: 0 }} title="attachment" />
              ) : (
                <img src={viewUrl} alt="attachment" className="max-h-[75vh] w-full rounded object-contain" />
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">{t('ReportsPOD:details.noAttachment')}</div>
        )}
      </Modal>
    </PageShell>
  )
}

export default PODReportsPage
